import React, { useState, useEffect, useCallback } from 'react';
import SectionExtractedDataEditor, { pruneForSave } from './SectionExtractedDataEditor';
import {
  getSchemaForSection,
  safeNormalizeSectionExtractedData,
  applySchemaDefaults,
  filterPositiveFindings,
  HIDE_SECTION_FINDINGS,
  deriveSectionDisplayStatus,
} from '../../constants/complianceSectionSchemas';
import {
  filterHumanReviewFlagsNotInFindings,
  filterDismissedHumanReviewFlags,
  humanReviewFlagText,
  humanReviewDismissPayload,
} from '../../utils/checklistFormatting';
import { FlagPageLinks, buildPageFileMap, DismissReviewButton } from './HumanReviewModal';
import { sectionDefaultFile } from '../common/citations/citationModel';

export const SERVICES_SUPPORTS_KEYS = [
  'services_lre',
  'accommodations',
  'assessment_participation',
];

export const SERVICES_SUPPORTS_MERGED_LABEL = 'Services, Modifications, and/or Supports';
export const SERVICES_SUPPORTS_MERGED_SUBTITLE =
  'Services & LRE · Accommodations · Assessment Participation';

const SECTION_HEADING_COLORS = {
  services_lre: 'text-indigo-700 dark:text-indigo-300',
  accommodations: 'text-violet-700 dark:text-violet-300',
  assessment_participation: 'text-teal-700 dark:text-teal-300',
};

const INPUT_CLS =
  'w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors';

const FILE_STATUS_STYLES = {
  ready: { label: 'Ready', dot: 'bg-green-500' },
  processing: { label: 'Processing', dot: 'bg-amber-400 animate-pulse' },
  failed: { label: 'Failed', dot: 'bg-red-500' },
};

function hasSourceMetadata(meta) {
  return meta && (meta.files?.length > 0 || meta.source_file);
}

function SectionSourcePanel({ meta, onOpenFileAtPage }) {
  if (!hasSourceMetadata(meta)) return null;

  return (
    <div>
      <h5 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Source Information</h5>
      <div className="rounded-lg bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 p-3 space-y-2 text-sm max-h-40 overflow-y-auto">
        {meta.files?.length > 0 ? (
          meta.files.map((f, i) => {
            const fileId = f.student_file_id || f.file_id;
            const pages = f.pages || [];
            const pageNums = f.page_numbers || [];
            return (
              <div key={i} className="flex items-start gap-2 pl-1 border-l-2 border-indigo-200 dark:border-indigo-700">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm">📄</span>
                    {fileId && onOpenFileAtPage ? (
                      <button
                        type="button"
                        onClick={() => onOpenFileAtPage(fileId, null, f.file_name)}
                        className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 underline break-all text-left text-xs"
                      >
                        {f.file_name}
                      </button>
                    ) : (
                      <span className="font-medium text-gray-800 dark:text-slate-200 break-all text-xs">{f.file_name}</span>
                    )}
                    {f.content_types?.map((ct, ci) => (
                      <span key={ci} className="px-1.5 py-0 rounded bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 text-[10px] font-medium">{ct}</span>
                    ))}
                  </div>
                  {(pages.length > 0 || pageNums.length > 0) && (
                    <div className="flex items-center gap-1 mt-1 ml-5 flex-wrap">
                      <span className="text-[10px] text-gray-400 dark:text-slate-500 uppercase font-semibold mr-0.5">Pages:</span>
                      {pages.length > 0
                        ? pages.map((pg, pi) => (
                          <button
                            key={pi}
                            type="button"
                            onClick={() => onOpenFileAtPage?.(pg.student_file_id || fileId, pg.page, f.file_name)}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
                          >
                            p. {pg.page}
                          </button>
                        ))
                        : pageNums.map((pn, pi) => (
                          <button
                            key={pi}
                            type="button"
                            onClick={() => fileId && onOpenFileAtPage?.(fileId, pn, f.file_name)}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-200 dark:hover:bg-indigo-800/40 transition-colors cursor-pointer"
                          >
                            p. {pn}
                          </button>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <>
            <div className="text-xs text-gray-700 dark:text-slate-200">
              <span className="text-gray-500 dark:text-slate-400">File: </span>
              {meta.source_file}
            </div>
            {meta.page_numbers?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {meta.page_numbers.map((pn, pi) => (
                  <span key={pi} className="px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-medium">
                    p. {pn}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
        {meta.generated_at && (
          <p className="text-[11px] text-gray-400 dark:text-slate-500 pt-1 border-t border-gray-200 dark:border-slate-700">
            Generated: {new Date(meta.generated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
        )}
      </div>
    </div>
  );
}

function SectionDocumentSelector({
  studentFiles,
  readyFiles,
  selectedDocIds,
  isOpen,
  onToggleOpen,
  onToggleDocSelect,
  onToggleSelectAll,
}) {
  if (!studentFiles.length) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggleOpen}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600 text-xs transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <svg className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-gray-700 dark:text-slate-300 truncate">
            {selectedDocIds.length === 0
              ? 'Include documents (optional)'
              : `${selectedDocIds.length} doc${selectedDocIds.length > 1 ? 's' : ''} selected`}
          </span>
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl z-30 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100 dark:border-slate-700 bg-gray-50/80 dark:bg-slate-900/60">
            <span className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Documents</span>
            <button
              type="button"
              onClick={onToggleSelectAll}
              className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium"
            >
              {selectedDocIds.length === readyFiles.length && readyFiles.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto py-1">
            {studentFiles.map((f) => {
              const st = FILE_STATUS_STYLES[f.status] || FILE_STATUS_STYLES.ready;
              const isReady = !f.status || f.status === 'ready';
              const isSelected = selectedDocIds.includes(f.id);
              return (
                <button
                  key={f.id}
                  type="button"
                  disabled={!isReady}
                  onClick={() => onToggleDocSelect(f.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs ${
                    isSelected ? 'bg-indigo-50 dark:bg-indigo-900/20' : isReady ? 'hover:bg-gray-50 dark:hover:bg-slate-700/50' : 'opacity-40 cursor-not-allowed'
                  }`}
                >
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 dark:border-slate-600'
                  }`}>
                    {isSelected && (
                      <svg className="w-2 h-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="truncate flex-1 text-gray-900 dark:text-white">{f.file_name}</span>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${st.dot}`} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    compliant: {
      label: 'Compliant',
      cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    needs_attention: {
      label: 'Needs Attention',
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    },
    non_compliant: {
      label: 'Non-Compliant',
      cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    },
  };
  const c = cfg[status] || cfg.non_compliant;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${c.cls}`}>
      {c.label}
    </span>
  );
}

function emptyEditState(section) {
  return {
    status: section?.status || 'compliant',
    findings: (section?.findings || []).map((f) => ({ ...f })),
    recommendations: (section?.recommendations || []).map((r) =>
      typeof r === 'string' ? r : r.description || '',
    ),
    missing_data: (section?.missing_data || []).map((m) =>
      typeof m === 'string' ? m : m.description || '',
    ),
    suggestions: (section?.suggestions || []).map((s) =>
      typeof s === 'string' ? s : s.description || '',
    ),
    extracted_data: {},
  };
}

function MergedSectionBlock({
  sectionKey,
  section,
  label,
  subtitle,
  isEditing,
  editState,
  onEditStateChange,
  onDismissFinding,
  onStartEdit,
  onCancelEdit,
  saving,
  studentFiles,
  readyFiles,
  selectedDocIds,
  docDropdownOpen,
  onToggleDocDropdown,
  onToggleDocSelect,
  onToggleSelectAll,
  isRerunning,
  onSectionRerun,
  onOpenFileAtPage,
  onOpenSource = null,
}) {
  const positiveFindings = HIDE_SECTION_FINDINGS.has(sectionKey)
    ? []
    : filterPositiveFindings(section?.findings || []);
  const actionableHr = (section?.human_review_flags || [])
    .map((f) => (typeof f === 'string' ? { code: f, message: f, severity: 'warning' } : f))
    .filter((f) => f && String(f.severity || 'warning').toLowerCase() !== 'info');
  const flags = filterDismissedHumanReviewFlags(
    filterHumanReviewFlagsNotInFindings(
      actionableHr,
      positiveFindings,
    ),
    section?.dismissed_findings,
  );
  const flagPageFileMap = buildPageFileMap(section?.source_metadata);

  if (!section) {
    return (
      <section className="rounded-xl border border-dashed border-gray-200 dark:border-slate-700 overflow-visible">
        <div className="p-6 text-center text-sm text-gray-400 dark:text-slate-500">
          No data for {label} yet.
        </div>
        {onSectionRerun && (
          <div className="px-4 pb-4 border-t border-gray-200 dark:border-slate-700 space-y-2">
            <SectionDocumentSelector
              studentFiles={studentFiles}
              readyFiles={readyFiles}
              selectedDocIds={selectedDocIds}
              isOpen={docDropdownOpen}
              onToggleOpen={onToggleDocDropdown}
              onToggleDocSelect={onToggleDocSelect}
              onToggleSelectAll={onToggleSelectAll}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => onSectionRerun(sectionKey)}
                disabled={isRerunning}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50"
              >
                {isRerunning ? 'Re-running...' : `Re-run Section${selectedDocIds.length > 0 ? ` (${selectedDocIds.length} docs)` : ''}`}
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-900/30 overflow-visible">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/80 flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h4 className={`text-sm font-bold ${SECTION_HEADING_COLORS[sectionKey] || 'text-gray-900 dark:text-white'}`}>
            {label}
          </h4>
          <p className="text-xs text-gray-500 dark:text-slate-400">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && <StatusBadge status={deriveSectionDisplayStatus(sectionKey, section)} />}
          {!isEditing && (
            <button
              type="button"
              onClick={() => onStartEdit(sectionKey)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-600/10 transition-colors"
              title={`Edit ${label}`}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
          )}
          {isEditing && (
            <button
              type="button"
              onClick={onCancelEdit}
              disabled={saving}
              className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-5 space-y-4">
        {isEditing ? (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Status</label>
              <select
                value={editState.status}
                onChange={(e) => onEditStateChange({ ...editState, status: e.target.value })}
                className={INPUT_CLS}
              >
                <option value="compliant">Compliant</option>
                <option value="needs_attention">Needs Attention</option>
                <option value="non_compliant">Non-Compliant</option>
              </select>
            </div>
            <SectionExtractedDataEditor
              sectionKey={sectionKey}
              data={editState.extracted_data}
              onChange={(extracted_data) => onEditStateChange({ ...editState, extracted_data })}
              onOpenFileAtPage={onOpenFileAtPage}
              onOpenSource={onOpenSource}
              sourceFileId={sectionDefaultFile(section?.source_metadata).id}
              sourceFileName={sectionDefaultFile(section?.source_metadata).name}
            />
          </>
        ) : (
          <>
            {flags.length > 0 && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1.5">Staff review</p>
                <ul className="space-y-1 text-xs text-amber-900 dark:text-amber-200/90">
                  {flags.map((flag, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <span>{humanReviewFlagText(flag)}</span>
                        <FlagPageLinks
                          pages={flag?.pages}
                          onOpenFileAtPage={onOpenFileAtPage}
              onOpenSource={onOpenSource}
                          pageFileMap={flagPageFileMap}
                        />
                      </div>
                      {onDismissFinding && (
                        <DismissReviewButton
                          onClick={() => {
                            const payload = humanReviewDismissPayload(flag);
                            if (payload) onDismissFinding(sectionKey, payload);
                          }}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {positiveFindings.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Findings</p>
                <ul className="space-y-1.5">
                  {positiveFindings.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-slate-300">
                      <svg className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="flex-1">{f.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <SectionExtractedDataEditor
              sectionKey={sectionKey}
              data={safeNormalizeSectionExtractedData(sectionKey, section)}
              onChange={() => {}}
              readOnly
              onOpenFileAtPage={onOpenFileAtPage}
              onOpenSource={onOpenSource}
              sourceFileId={sectionDefaultFile(section?.source_metadata).id}
              sourceFileName={sectionDefaultFile(section?.source_metadata).name}
            />

            <SectionSourcePanel
              meta={section.source_metadata}
              onOpenFileAtPage={onOpenFileAtPage}
              onOpenSource={onOpenSource}
            />
          </>
        )}
      </div>

      {!isEditing && onSectionRerun && (
        <div className="px-4 sm:px-5 pb-4 border-t border-gray-200 dark:border-slate-700 pt-3 space-y-2">
          <SectionDocumentSelector
            studentFiles={studentFiles}
            readyFiles={readyFiles}
            selectedDocIds={selectedDocIds}
            isOpen={docDropdownOpen}
            onToggleOpen={onToggleDocDropdown}
            onToggleDocSelect={onToggleDocSelect}
            onToggleSelectAll={onToggleSelectAll}
          />
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-gray-400 dark:text-slate-500">
              {section.issues_count != null
                ? `${section.issues_count} issue${section.issues_count !== 1 ? 's' : ''}`
                : 'Leave empty to use all documents'}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => onSectionRerun(sectionKey)}
                disabled={isRerunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold disabled:opacity-50"
              >
                {isRerunning ? 'Re-running...' : `Re-run Section${selectedDocIds.length > 0 ? ` (${selectedDocIds.length} docs)` : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default function ServicesSupportsMergedModal({
  open,
  compliance,
  sectionConfig,
  onClose,
  onSave,
  onSaveComplete,
  onDismissFinding,
  studentFiles = [],
  onCrossDocRerun,
  onRerun,
  rerunningSections,
  onOpenFileAtPage,
  onOpenSource = null,
  variant = 'modal', cycleLabel,
}) {
  const isPage = variant === 'page';
  const [editingKey, setEditingKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editByKey, setEditByKey] = useState({});
  const [docDropdownKey, setDocDropdownKey] = useState(null);
  const [selectedDocIdsByKey, setSelectedDocIdsByKey] = useState({});
  const readyFiles = studentFiles.filter((f) => !f.status || f.status === 'ready');

  const getSelectedDocs = (key) => selectedDocIdsByKey[key] || [];

  const toggleDocSelect = (key, fileId) => {
    setSelectedDocIdsByKey((prev) => {
      const current = prev[key] || [];
      const next = current.includes(fileId)
        ? current.filter((id) => id !== fileId)
        : [...current, fileId];
      return { ...prev, [key]: next };
    });
  };

  const toggleSelectAll = (key) => {
    const readyIds = readyFiles.map((f) => f.id);
    setSelectedDocIdsByKey((prev) => {
      const current = prev[key] || [];
      return { ...prev, [key]: current.length === readyIds.length ? [] : readyIds };
    });
  };

  const handleSectionRerun = async (sectionKey) => {
    const documentIds = getSelectedDocs(sectionKey);
    try {
      if (documentIds.length > 0 && onCrossDocRerun) {
        await onCrossDocRerun(sectionKey, documentIds);
        setSelectedDocIdsByKey((prev) => ({ ...prev, [sectionKey]: [] }));
      } else if (onRerun) {
        await onRerun(sectionKey);
      }
      setDocDropdownKey(null);
    } catch {
      /* parent alert */
    }
  };

  const startEditing = useCallback((key) => {
    const section = compliance?.[key];
    const base = emptyEditState(section);
    try {
      const normalized = section ? safeNormalizeSectionExtractedData(key, section) : {};
      base.extracted_data = applySchemaDefaults(key, normalized);
    } catch {
      base.extracted_data = applySchemaDefaults(key, {});
    }
    setEditByKey((prev) => ({ ...prev, [key]: base }));
    setEditNotes('');
    setEditError('');
    setEditingKey(key);
    setDocDropdownKey(null);
  }, [compliance]);

  const cancelEditing = () => {
    setEditingKey(null);
    setEditError('');
    setEditNotes('');
  };

  const handleSave = async () => {
    if (!editingKey) return;
    setSaving(true);
    setEditError('');
    try {
      const state = editByKey[editingKey];
      if (!state) return;
      const schema = getSchemaForSection(editingKey);
      const payload = {
        status: state.status,
        findings: state.findings.filter((f) => f.description?.trim()),
        recommendations: state.recommendations.filter((r) => r.trim()),
        missing_data: state.missing_data.filter((m) => m.trim()),
        suggestions: state.suggestions.filter((s) => s.trim()),
        extracted_data: schema
          ? pruneForSave(state.extracted_data, schema, editingKey)
          : state.extracted_data,
      };
      if (editNotes.trim()) payload.notes = editNotes.trim();
      await onSave(editingKey, payload, { silent: true });
      setEditingKey(null);
      setEditNotes('');
      onSaveComplete?.();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setEditingKey(null);
      setDocDropdownKey(null);
      setSelectedDocIdsByKey({});
    }
  }, [open]);

  useEffect(() => {
    if (!open || isPage) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (editingKey) cancelEditing();
        else onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, editingKey, onClose, isPage]);

  if (!open) return null;

  const totalIssues = SERVICES_SUPPORTS_KEYS.reduce((sum, key) => {
    const sec = compliance?.[key];
    return sum + (sec?.issues_count || 0);
  }, 0);

  const cardClass = isPage
    ? 'relative bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl w-full flex flex-col'
    : 'relative bg-white dark:bg-slate-800 shadow-2xl border border-gray-200 dark:border-slate-700 rounded-none sm:rounded-2xl w-full max-w-4xl sm:mx-4 max-h-[100vh] sm:max-h-[92vh] flex flex-col';

  const card = (
      <div className={cardClass}>
        <div className="flex items-start sm:items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 dark:border-slate-700 flex-shrink-0 gap-2">
          <div className="min-w-0 flex items-center gap-2">
            {isPage && (
              <button type="button" onClick={onClose} title="Back" className="hidden lg:flex p-1.5 -ml-1 rounded-lg text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                  {SERVICES_SUPPORTS_MERGED_LABEL}
                </h3>
                {isPage && cycleLabel && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">{cycleLabel}</span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
                {SERVICES_SUPPORTS_MERGED_SUBTITLE}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editingKey && totalIssues > 0 && (
              <span className="px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
              </span>
            )}
            {!isPage && (
              <button
                type="button"
                onClick={() => {
                  if (editingKey) cancelEditing();
                  else onClose();
                }}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className={`${isPage ? '' : 'flex-1 overflow-y-auto'} p-4 sm:p-6 space-y-6`}>
          {editError && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
              {editError}
            </div>
          )}

          {SERVICES_SUPPORTS_KEYS.map((key) => {
            const cfg = sectionConfig[key] || {};
            return (
              <MergedSectionBlock
                key={key}
                sectionKey={key}
                section={compliance?.[key]}
                label={cfg.label || key}
                subtitle={cfg.subtitle || ''}
                isEditing={editingKey === key}
                editState={editByKey[key]}
                onEditStateChange={(next) =>
                  setEditByKey((prev) => ({ ...prev, [key]: next }))
                }
                onDismissFinding={onDismissFinding}
                onStartEdit={startEditing}
                onCancelEdit={cancelEditing}
                saving={saving}
                studentFiles={studentFiles}
                readyFiles={readyFiles}
                selectedDocIds={getSelectedDocs(key)}
                docDropdownOpen={docDropdownKey === key}
                onToggleDocDropdown={() => setDocDropdownKey((prev) => (prev === key ? null : key))}
                onToggleDocSelect={(fileId) => toggleDocSelect(key, fileId)}
                onToggleSelectAll={() => toggleSelectAll(key)}
                isRerunning={rerunningSections?.has(key)}
                onSectionRerun={handleSectionRerun}
                onOpenFileAtPage={onOpenFileAtPage}
                onOpenSource={onOpenSource}
              />
            );
          })}

          {editingKey && (
            <div>
              <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Edit Notes</label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={2}
                placeholder="Reason for this edit..."
                className={`${INPUT_CLS} resize-none`}
              />
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 dark:border-slate-700 px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 flex-shrink-0">
          {editingKey ? (
            <>
              <button
                type="button"
                onClick={cancelEditing}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 shadow-sm"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium"
            >
              {isPage ? 'Back' : 'Close'}
            </button>
          )}
        </div>
      </div>
  );

  if (isPage) return card;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => {
          if (!editingKey) onClose();
        }}
      />
      {card}
    </div>
  );
}
